import { motion } from 'framer-motion'
import { MessageSquarePlus, Activity, Inbox, ArrowRight } from 'lucide-react'

interface OnboardingStepWelcomeProps {
  onContinue: () => void
  onSkip: () => void
}

const features = [
  {
    icon: MessageSquarePlus,
    title: 'Visual Feedback',
    description: 'Drop a widget on your site. Clients click, comment, and annotate — no accounts needed.',
  },
  {
    icon: Activity,
    title: 'Monitoring & Signals',
    description: 'Automated performance, SEO, uptime, and SSL checks. Issues become inbox items.',
  },
  {
    icon: Inbox,
    title: 'Unified Inbox',
    description: 'Every ticket, suggestion, and alert in one feed. Triage with keyboard shortcuts.',
  },
]

export function OnboardingStepWelcome({ onContinue, onSkip }: OnboardingStepWelcomeProps) {
  return (
    <motion.div
      key="step-welcome"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto"
    >
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-framed-neutral-800 tracking-tight font-display mb-3">
          Let's give your project brains
        </h1>
        <p className="text-base text-framed-neutral-500 max-w-lg mx-auto">
          Everything your website needs — feedback, monitoring, and project management in one place.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 + index * 0.1 }}
            className="bg-white rounded-xl border border-framed-neutral-300 p-5 text-center"
          >
            <div className="w-10 h-10 rounded-lg bg-framed-neutral-100 flex items-center justify-center mx-auto mb-3">
              <feature.icon className="w-5 h-5 text-framed-neutral-600" />
            </div>
            <h3 className="text-sm font-semibold text-framed-neutral-800 mb-1.5">
              {feature.title}
            </h3>
            <p className="text-xs text-framed-neutral-500 leading-relaxed">
              {feature.description}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="flex flex-col items-center gap-3">
        <button
          onClick={onContinue}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-framed-neutral-800 text-white rounded-lg font-medium hover:bg-framed-neutral-900 hover:scale-[1.02] transition-all"
        >
          Get started
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={onSkip}
          className="text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors"
        >
          I'll explore on my own
        </button>
      </div>
    </motion.div>
  )
}
